import {
    Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { FadedPaper } from '../../theme/StyledFields';
import VizToggle, { VizType } from '../team-report/VizToggle';
import { AthleteTimeline, formatDuration } from './types';

interface Props {
    athletes: AthleteTimeline[];
}

type ChartRow = {
    name: string;
    sessionsCompleted: number;
    sessionDetailLines: string[];
};

const AthleteSessionsCompletedSection: React.FC<Props> = ({ athletes }) => {
    const [viz, setViz] = useState<VizType>('bar');
    const theme = useTheme();

    const sorted = useMemo(
        () => [...athletes].sort((a, b) => a.athlete_name.localeCompare(b.athlete_name)),
        [athletes],
    );

    const chartData: ChartRow[] = useMemo(
        () =>
            sorted.map((a) => ({
                name: a.athlete_name,
                sessionsCompleted: a.sessions.length,
                sessionDetailLines: a.sessions
                    .slice()
                    .sort((s1, s2) => s1.session_number - s2.session_number)
                    .map(
                        (s) =>
                            `Session ${s.session_number} · ${formatDuration(s.session_duration_seconds)}`,
                    ),
            })),
        [sorted],
    );

    const maxSessions = Math.max(1, ...chartData.map((d) => d.sessionsCompleted));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-athlete-sessions-completed">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">All athletes: sessions completed</Typography>
                <VizToggle value={viz} options={['bar', 'table']} onChange={setViz} />
            </Box>

            {viz === 'bar' && chartData.length > 0 && (
                <ResponsiveContainer width="100%" height={Math.max(240, chartData.length * 40)}>
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ left: 8, right: 16, top: 8, bottom: 8 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            type="number"
                            domain={[0, maxSessions]}
                            allowDecimals={false}
                            label={{ value: 'Sessions completed', position: 'bottom', offset: 0 }}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={148}
                            tick={{ fontSize: 11 }}
                            interval={0}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const row = payload[0].payload as ChartRow;
                                return (
                                    <Box
                                        sx={{
                                            bgcolor: 'background.paper',
                                            p: 1.5,
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            maxWidth: 320,
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            {row.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {row.sessionsCompleted} session
                                            {row.sessionsCompleted === 1 ? '' : 's'} completed
                                        </Typography>
                                        {row.sessionDetailLines.map((line) => (
                                            <Typography key={line} variant="caption" display="block">
                                                {line}
                                            </Typography>
                                        ))}
                                    </Box>
                                );
                            }}
                        />
                        <Bar
                            dataKey="sessionsCompleted"
                            name="Sessions"
                            radius={[0, 4, 4, 0]}
                            fill={theme.palette.primary.main}
                        />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Athlete</TableCell>
                                <TableCell align="right">Sessions</TableCell>
                                <TableCell>Completed sessions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((a) => (
                                <TableRow key={a.athlete_id}>
                                    <TableCell sx={{ fontWeight: 600 }}>{a.athlete_name}</TableCell>
                                    <TableCell align="right">{a.sessions.length}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {a.sessions
                                                .slice()
                                                .sort((s1, s2) => s1.session_number - s2.session_number)
                                                .map((s) => (
                                                    <Chip
                                                        key={s.session_number}
                                                        size="small"
                                                        variant="outlined"
                                                        label={`Session ${s.session_number} · ${formatDuration(
                                                            s.session_duration_seconds,
                                                        )}`}
                                                    />
                                                ))}
                                            {a.sessions.length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    None
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </FadedPaper>
    );
};

export default AthleteSessionsCompletedSection;
